import { useCallback, useEffect, useRef, useState } from "react";

const useAudioPlayer = () => {

  const audioRef = useRef<HTMLAudioElement | null>(null)

  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)

  const addUpdateTimeEventListener = useCallback(() => {
    const audio = audioRef.current
    if (!audio) { return }
    setCurrentTime(audio.currentTime)
  }, [])

  const addUpdateDurationEventListener = useCallback(() => {
    const audio = audioRef.current
    if (!audio) { return }
    setDuration(audio.duration)
  }, [])

  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    audio.addEventListener('timeupdate', addUpdateTimeEventListener)
    audio.addEventListener('loadedmetadata', addUpdateDurationEventListener)

    return () => {
      audio.removeEventListener('timeupdate', addUpdateTimeEventListener)
      audio.removeEventListener('loadedmetadata', addUpdateDurationEventListener)
    }
  }, [addUpdateDurationEventListener, addUpdateTimeEventListener])

  useEffect(() => {
    if (currentTime === duration) {
      setIsPlaying(false)
    }
  }, [currentTime, duration])

  const onPlay = () => {
    const audio = audioRef.current
    if (audio && !isPlaying) {
      audio.play()
      setIsPlaying(true)
    }
  }

  const onPause = useCallback(() => {
    const audio = audioRef.current
    if (audio && isPlaying) {
      audio.pause()
      setIsPlaying(false)
    }
  }, [isPlaying])

  const onCancel = useCallback(() => {
    if (!audioRef.current) { return }
    const audio = audioRef.current
    audio.removeEventListener('timeupdate', addUpdateTimeEventListener)
    audio.removeEventListener('loadedmetadata', addUpdateDurationEventListener)
    audio.load()
    audio.addEventListener('timeupdate', addUpdateTimeEventListener)
    audio.addEventListener('loadedmetadata', addUpdateDurationEventListener)
    setCurrentTime(0)
    setIsPlaying(false)
  }, [])

  return [{
    audioRef,
    isPlaying,
    currentTime,
    duration,
    onPlay,
    onPause,
    onCancel
  }]
}

export default useAudioPlayer